import { Keyboard, LogOut, Monitor, Moon, MoreHorizontal, Sun, UserRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { sectionHref } from '@/features/settings/sections';
import { useEntitlements, useSession } from '@/lib/entitlements';
import { THEMES, THEME_LABELS, useTheme, type Theme } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { ROLE_LABELS } from '@/types';
import { useShell } from './shell-context';
import { TenantSwitcherMenu } from './tenant-switcher';

const THEME_ICONS: Record<Theme, typeof Sun> = { light: Sun, dark: Moon, system: Monitor };

export function UserMenu({ collapsed = false }: { collapsed?: boolean }) {
  const { tenant, user, showsInNav } = useEntitlements();
  const { signOut } = useSession();
  const { setShortcutsOpen } = useShell();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();

  const ThemeIcon = THEME_ICONS[theme];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={`Cuenta de ${user.name}`}
        className={cn(
          'flex items-center rounded-md transition-colors duration-150 ease-out hover:bg-sidebar-accent/60 data-[state=open]:bg-sidebar-accent/60',
          collapsed ? 'size-8 justify-center' : 'h-12 w-full gap-2 px-2 text-left',
        )}
      >
        <UserAvatar name={user.name} size={collapsed ? 'default' : 'sm'} />
        {!collapsed && (
          <>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium">{user.name}</span>
              <span className="truncate text-xs text-muted-foreground">{tenant.name}</span>
            </span>
            <MoreHorizontal className="size-4 shrink-0 text-muted-foreground" />
          </>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" side="top" sideOffset={8} className="w-60">
        <DropdownMenuLabel className="flex flex-col gap-0.5 py-2 font-normal">
          <span className="truncate text-sm font-medium text-foreground">{user.name}</span>
          <span className="truncate text-xs text-muted-foreground">{user.email}</span>
          <span className="truncate text-xs text-muted-foreground">
            {ROLE_LABELS[user.role]} · {tenant.name}
          </span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Straight to the personal section: landing on the company profile is
            not «mi cuenta», it is the organisation's.

            `showsInNav` y no `hasModule`: este ítem OFRECE un destino, igual que
            el menú lateral, así que mira también `hiddenNavModules`. */}
        {showsInNav('settings') && (
          <DropdownMenuItem onSelect={() => navigate(sectionHref('account'))}>
            <UserRound />
            Mi cuenta
          </DropdownMenuItem>
        )}

        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <ThemeIcon className="size-4 text-muted-foreground" />
            Tema
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup value={theme} onValueChange={(next) => setTheme(next as Theme)}>
              {THEMES.map((option) => (
                <DropdownMenuRadioItem key={option} value={option}>
                  {THEME_LABELS[option]}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuItem onSelect={() => setShortcutsOpen(true)}>
          <Keyboard />
          Atajos de teclado
          <DropdownMenuShortcut>?</DropdownMenuShortcut>
        </DropdownMenuItem>

        <TenantSwitcherMenu />
        <DropdownMenuSeparator />

        <DropdownMenuItem
          variant="destructive"
          onSelect={() => {
            signOut();
            navigate('/login', { replace: true });
          }}
        >
          <LogOut />
          Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
